import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { Card } from './SubscriptionAppCard';
type CustomStyleMode = 'none' | 'external' | 'inline';
interface CustomStylesheetSectionProps {
  /** What the styles apply to, used in the "no custom styles" explanation. */
  surfaceLabel?: string;
  /** Placeholder shown in the inline CSS editor. */
  cssPlaceholder?: string;
  /** Render without the surrounding Card, for use inside an existing panel. */
  bare?: boolean;
}
const modes: {
  id: CustomStyleMode;
  label: string;
}[] = [{
  id: 'none',
  label: 'No custom styles'
}, {
  id: 'external',
  label: 'External URL'
}, {
  id: 'inline',
  label: 'Inline CSS'
}];
export function CustomStylesheetSection({
  surfaceLabel = 'widget',
  cssPlaceholder = '.ocean-widget { ... }',
  bare = false
}: CustomStylesheetSectionProps) {
  const [customStyleMode, setCustomStyleMode] = useState<CustomStyleMode>('none');
  const body = <div className={bare ? '' : 'p-6'} data-id="element-2247" data-mp="26p79ahm33acl">
      <div className="flex items-center justify-between mb-4" data-id="element-2248" data-mp="34yka7bdjnr2a">
        <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-2249" data-mp="34y0ttz3jlaqd">
          Custom Stylesheet
        </h2>
        <span className="px-2 py-0.5 bg-[#F3E8FF] text-[#7C3AED] text-[11px] font-bold rounded uppercase tracking-wider" data-id="element-2250" data-mp="39vanxs1vmakj">
          Agency
        </span>
      </div>
      <p className="text-[13px] text-[#64748B] mb-6" data-id="element-2251" data-mp="3jlvkzb1wj71c">
        For agencies: replace the default {surfaceLabel} styles entirely with
        your own CSS. No overrides, no fragility.
      </p>

      <div className="flex border border-[#E2E8F0] rounded-lg overflow-hidden mb-6" data-id="element-2252" data-mp="2qzkke0eap64j">
        {modes.map(mode => <button key={mode.id} type="button" onClick={() => setCustomStyleMode(mode.id)} className={`
                  flex-1 py-2.5 text-[13px] font-medium transition-colors
                  ${customStyleMode === mode.id ? 'bg-[#F0F9FF] text-[#0EA5E9] border-b-2 border-[#0EA5E9]' : 'bg-white text-[#64748B] hover:bg-[#F8FAFC] border-b-2 border-transparent'}
                `} data-id="element-2253" data-mp="2vwt62aynjsaj">
            {mode.label}
          </button>)}
      </div>

      {customStyleMode === 'none' && <div className="bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg p-4 flex gap-3" data-id="element-2254" data-mp="27b1h7r9v65yx">
          <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-2255" data-mp="2la16901agmvm" />
          <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-2256" data-mp="2l86sq6scjx1k">
            The {surfaceLabel} uses the brand colours, typography, and component
            styling configured above. This works for most merchants.
          </p>
        </div>}

      {customStyleMode === 'external' && <div data-id="element-2257" data-mp="1ollcerr5brcp">
          <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-2258" data-mp="1ohxm01gzzl27">
            Stylesheet URL
          </label>
          <input type="text" placeholder="https://example.com/styles/widget.css" className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-2259" data-mp="1xkyqxyu4n64a" />
        </div>}

      {customStyleMode === 'inline' && <div data-id="element-2260" data-mp="3giuzrfhopovj">
          <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-2261" data-mp="3gj4o6yn2znz4">
            CSS
          </label>
          <textarea rows={6} placeholder={cssPlaceholder} className="w-full p-3 border border-[#CBD5E1] rounded-lg text-[14px] font-mono text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-2262" data-mp="3lawi9j5g4bu0" />
        </div>}
    </div>;
  if (bare) {
    return body;
  }
  return <Card padding="none" className="border border-[#E2E8F0]" data-id="element-2263" data-mp="43aut3lau9d4">
      {body}
    </Card>;
}